/**
 * 会場ごとの地図設定。
 *
 * オフライン地図（PMTiles）の切り出し範囲と初期表示位置をここで決める。
 * 範囲は `ref/map_*.txt` の校正点と `doc_*.kml` のゴール地点が
 * すべて収まるように取り、周囲に余白を足してある。
 */

export interface VenueMapConfig {
  id: string;
  name: string;
  /** 初期表示の中心 [経度, 緯度] */
  center: [number, number];
  initialZoom: number;
  /** オフライン地図の切り出し範囲 [西, 南, 東, 北] */
  bbox: [number, number, number, number];
  /** 切り出すズーム範囲。上限を上げるほどファイルが大きくなる */
  minZoom: number;
  maxZoom: number;
  /** UTM ゾーン */
  utmZone: number;
  /**
   * 高度の色分け・凡例の刻み幅（フィート）。会場（エリア）ごとに高度制限が
   * 異なるため、既定値を上書きできるようにしてある。省略時は layers.ts の
   * DEFAULT_ALTITUDE_STEP_FT を使う。
   */
  altitudeStepFeet?: number;
  note?: string;
  /**
   * PZ/SA情報パネルの出所リンクを上書きする。`pzsa.json`自体の`source`は
   * git管理外（`apps/web/public/venues/`）で会場ごとに手動配置のため、
   * リンク先を直すためだけに毎回scp+ビルドをするのは事故が起きやすい
   * （実際にVPS側での配置漏れ・キャッシュにより「直したのに変わらない」
   * 事故が発生した、docs/08 §117-118）。ここ（git管理下・通常デプロイで
   * 反映される）を優先させることで、リンクの修正だけなら通常のデプロイで
   * 完結するようにする。省略時は`pzsa.json`の`source`をそのまま使う。
   */
  pzsaSourceUrl?: string;
}

/**
 * 渡良瀬。
 *
 * 中心は**藤岡運動公園のソフトボール場**（36°15'12.2"N 139°39'17.0"E）。
 *
 * 範囲の根拠
 *   校正点（map_watarase.txt）    緯度 36.1607〜36.3069 / 経度 139.5943〜139.7983
 *   ゴール370点（doc_watarase.kml）緯度 36.1383〜36.3069 / 経度 139.5700〜139.8856
 *   → 両方を含む範囲に約2kmの余白を足した（約 32 × 23 km）
 */
export const VENUE_MAPS: Record<string, VenueMapConfig> = {
  watarase: {
    id: 'watarase',
    name: '渡良瀬',
    center: [139.654722, 36.253389],
    initialZoom: 12,
    bbox: [139.548, 36.12, 139.908, 36.325],
    minZoom: 9,
    // 15だと建物・小径が粗く見えるとの指摘（2026-08-08）を受けて17に引き上げ。
    // ファイルサイズが増えるので、再生成後に実測して大きすぎないか確認すること
    maxZoom: 17,
    utmZone: 54,
    note: '中心は藤岡運動公園のソフトボール場',
  },
  krosno: {
    id: 'krosno',
    name: '世界選手権クロスノ',
    // 中心は競技地図の切り出し範囲の幾何中心（暫定）。
    // 離陸場所が判明したらそちらへ差し替えること
    center: [21.7518, 49.6815],
    initialZoom: 12,
    // 当初は競技地図の四隅（2026WorldsMap.map校正値）に約2kmの余白を足していたが、
    // クロスノは競技エリア自体が広く（約38×36km）そこまで大きくはみ出す可能性は
    // 低いという利用者判断（2026-09-06）により、余白を無くして競技地図の実測四隅
    // そのものに縮小した（33.2MB→26.3MB）。渡良瀬は競技地図が小さく逆に余白を
    // 大きく取る必要があったための会場なので、この方針は会場非依存にしないこと
    bbox: [21.491931, 49.527989, 22.011671, 49.83503],
    minZoom: 9,
    // watarase同様17から始める。渡良瀬(32×23km)よりひと回り広い(約38×34km)ため、
    // 生成後に必ずファイルサイズを実測し、大きすぎれば下げること
    maxZoom: 17,
    utmZone: 34,
    note: '範囲の根拠は2026WorldsMap.map（OziExplorer校正）の四隅実測値そのもの（余白なし）',
    pzsaSourceUrl: 'https://watchmefly.net/events/event.php?e=worlds2026&v=enb',
  },
  ichinoseki: {
    id: 'ichinoseki',
    name: '一関',
    // 中心はコモン・ローンチ・ポイント（CLP、GB Note記載の1380-0950をグリッド解決した座標）
    center: [141.15922, 38.93432],
    initialZoom: 12,
    // bboxの根拠はIchinoseki2026-101.map（OziExplorer校正）の四隅実測値そのもの
    // （クロスノと同じ方針。約53×53kmあり余白を足す必要は無いという判断）
    bbox: [140.884233, 38.694622, 141.49779, 39.173169],
    minZoom: 9,
    // クロスノ（約38×36km）よりひと回り広いため、生成後に必ずファイルサイズを
    // 実測し、大きすぎれば下げること
    maxZoom: 17,
    utmZone: 54,
    note: 'bboxの根拠はIchinoseki2026-101.map（OziExplorer校正）の四隅実測値そのもの（余白なし）',
  },
  saga: {
    id: 'saga',
    name: '佐賀',
    // 中心はコモン・ローンチ・ポイント（CLP、Rules記載の1600/8000をグリッド解決した座標）
    center: [130.245283, 33.252762],
    initialZoom: 11,
    // bboxの根拠はSaga_Map_ver2024-2.map（OziExplorer校正）の四隅実測値そのもの
    // （約50×35km、余白なし）
    bbox: [129.952856, 33.090213, 130.488006, 33.410609],
    minZoom: 9,
    maxZoom: 17,
    utmZone: 52,
    note: 'bboxの根拠はSaga_Map_ver2024-2.map（OziExplorer校正）の四隅実測値そのもの（余白なし）。2026年大会は2024年作成の競技地図をそのまま流用',
  },
};

export function mapConfigOf(venueId: string): VenueMapConfig | null {
  return VENUE_MAPS[venueId] ?? null;
}

export interface VenueLink {
  /** 画面に出す短い名前（1行に並べるので2〜4文字が目安） */
  label: string;
  url: string;
  /** 長押し・ホバーで出す説明（任意） */
  title?: string;
}

/**
 * 大会ごとの公式ページへのリンク（左のパネルの「大会のページ」に、選んでいる会場のものだけを出す）。
 * 名前は短く（「結果」「競技者」…）。1行に並べて出すので、長いと行を使いすぎる。
 * 結果・競技者一覧は eKLIPS の公式サイト（競技者一覧は計測の照合にも使う、docs/08 §200）、
 * ライブの位置は WatchMeFly。URLは公式の案内（本部の連絡）で示されたもの（docs/08 §219）
 */
export const VENUE_LINKS: Record<string, VenueLink[]> = {
  saga: [
    { label: '結果', url: 'https://www.sibf.biz/2026Results/SA26Index.html', title: '公式の結果（2026 佐賀インターナショナルバルーンフェスタ）' },
    { label: '競技者', url: 'https://www.sibf.biz/2026Results/SA26CompetitorList.html', title: '競技者一覧' },
    { label: '地図', url: 'https://www.sibf.biz/2026Results/SA26CompetitionMap.html', title: '競技地図（PZ・SAの公式の形）' },
    { label: 'WatchMeFly', url: 'https://watchmefly.net/events/event.php?e=saga2026', title: 'WatchMeFly（イベント・ライブの位置）' },
  ],
  ichinoseki: [
    { label: '結果', url: 'http://www.exem-balloon.com/Ichinoseki26Results/', title: '公式の結果（一関・平泉バルーンフェスティバル2026）' },
    { label: '競技者', url: 'http://www.exem-balloon.com/Ichinoseki26Results/IC26CompetitorList.html', title: '競技者一覧' },
    { label: '大会情報', url: 'http://www.air-b.com/pinfo/ichinoseki2026/index.html', title: '大会情報（air-b.com）' },
  ],
};

/** 会場のリンク。無ければ空 */
export function venueLinksOf(venueId: string): VenueLink[] {
  return VENUE_LINKS[venueId] ?? [];
}

/** 切り出し範囲の広さ [km] */
export function bboxSizeKm(bbox: [number, number, number, number]): { width: number; height: number } {
  const [west, south, east, north] = bbox;
  const midLat = ((south + north) / 2) * (Math.PI / 180);
  return {
    width: ((east - west) * 111.32 * Math.cos(midLat)),
    height: (north - south) * 110.574,
  };
}
